import _ from 'lodash';

export interface ClinicDataType extends google.maps.LatLngLiteral {
  name: string;
  description?: string;
  hours: string;
  // Some clinics missing website
  website?: string;
  address: string;
  telephone: string[];
}

type Digit = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
type Hours = `${0 | 1}${Digit}` | `2${0 | 1 | 2 | 3}`;
type Minutes = `${0 | 1 | 2 | 3 | 4 | 5}${Digit}`;
export type TimeString = `${Hours}:${Minutes}`;

export interface DayHours {
  // Could be replaced by `string` but this is more fun :)
  openHour: TimeString;
  closeHour: TimeString;
  open: boolean;
}

export interface DFDClinicDataType extends ClinicDataType {
  dfdLink: string;
  dfdHours: [
    DayHours,
    DayHours,
    DayHours,
    DayHours,
    DayHours,
    DayHours,
    DayHours
  ];
}

export const isDFDClinic = (
  clinic: ClinicDataType
): clinic is DFDClinicDataType => {
  return _.has(clinic, 'dfdLink');
};

export type Location = Omit<
  DFDClinicDataType,
  'dfdLink' | 'website' | 'telephone'
>;

export interface OFDClinic {
  name: string;
  dfdLink: string;
  website: string;
  telephone: string[];
  locations: Location[];
}

export const OFD_CLINICS: OFDClinic[] = [
  {
    name: 'ASCO RAAM Clinic',
    dfdLink: 'https://helpwithaddictions.accessraam.ca/',
    website:
      'https://helpwithaddictions.ca/i-need-help-with/get-help/addiction-withdrawal-management-services/rapid-access-addiction-medicine-raam/',
    telephone: ['1-800-263-2288;322', '905-841-7007;322'],
    locations: [
      {
        name: 'Humber River Hospital RAAM',
        lat: 43.7237804,
        lng: -79.4887557,
        hours: 'Walk-in hours: Thursday, 12:00-3:00 pm.',
        address: '1235 Wilson Ave, 5th Fl., Toronto, ON M3M 0B2',
        dfdHours: [
          { openHour: '00:00', closeHour: '23:59', open: false }, // Sunday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Monday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Tuesday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Wednesday
          { openHour: '12:00', closeHour: '15:00', open: true }, // Thursday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Friday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Saturday
        ],
      },
      {
        name: 'Mackenzie Health RAAM',
        lat: 43.8704455,
        lng: -79.4508084,
        hours: 'Drop-in hours: Mon 9-10am. Booked appointments: Mon 9-11am.',
        address: '10 Trench St., B Wing, Level 3, Richmond Hill, ON L4C 4Z3',
        dfdHours: [
          { openHour: '00:00', closeHour: '23:59', open: false }, // Sunday
          { openHour: '09:00', closeHour: '11:00', open: true }, // Monday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Tuesday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Wednesday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Thursday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Friday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Saturday
        ],
      },
      {
        name: 'North York General Hospital',
        lat: 43.7695699,
        lng: -79.3632018,
        hours:
          'Drop-in (in person and virtual): Tues 11am-1pm. Scheduled appointments (in person and virtual): Mon 9am-4pm, Tues 9am-3pm.',
        address:
          '4001 Leslie St., 8th Floor (Ambulatory Mental Health), Toronto.',
        dfdHours: [
          { openHour: '00:00', closeHour: '23:59', open: false }, // Sunday
          { openHour: '09:00', closeHour: '16:00', open: true }, // Monday
          { openHour: '19:00', closeHour: '15:00', open: true }, // Tuesday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Wednesday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Thursday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Friday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Saturday
        ],
      },
    ],
  },
  {
    name: 'Health Sciences North',
    dfdLink: 'https://hsnsudbury.accessraam.ca/',
    website:
      'https://hsnsudbury.ca/en/Services-and-Specialties/Mental-Health-and-Addictions/Rapid-Access-Addictions-Medicine-RAAM-Clinic',
    telephone: ['705-523-7100;1875'],
    locations: [
      {
        name: 'Health Sciences North RAAM Clinic',
        lat: 46.4919155,
        lng: -80.9922157,
        hours:
          'Walk-in and scheduled in person, OTN and phone appointments Mon-Fri 8am-3pm.',
        address: '127 Cedar St, Sudbury.',
        dfdHours: [
          { openHour: '00:00', closeHour: '23:59', open: false }, // Sunday
          { openHour: '08:00', closeHour: '15:00', open: true }, // Monday
          { openHour: '08:00', closeHour: '15:00', open: true }, // Tuesday
          { openHour: '08:00', closeHour: '15:00', open: true }, // Wednesday
          { openHour: '08:00', closeHour: '15:00', open: true }, // Thursday
          { openHour: '08:00', closeHour: '15:00', open: true }, // Friday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Saturday
        ],
      },
    ],
  },
  {
    name: 'Lakeridge Health',
    dfdLink: 'https://lh.accessraam.ca/',
    website:
      'https://www.lakeridgehealth.on.ca/en/ourservices/rapid-access-addiction-medicine--raam-.asp',
    telephone: ['905-576-8711;RAAM'],
    locations: [
      {
        name: 'Lakeridge Health RAAM Clinic',
        lat: 43.88884,
        lng: -78.8624131,
        hours:
          'No drop-in hours. Scheduled virtual and in-person appointments: Mon-Fri 9am-5pm.',
        address: '300 Centre St. South, Oshawa.',
        dfdHours: [
          { openHour: '00:00', closeHour: '23:59', open: false }, // Sunday
          { openHour: '09:00', closeHour: '17:00', open: true }, // Monday
          { openHour: '09:00', closeHour: '17:00', open: true }, // Tuesday
          { openHour: '09:00', closeHour: '17:00', open: true }, // Wednesday
          { openHour: '09:00', closeHour: '17:00', open: true }, // Thursday
          { openHour: '09:00', closeHour: '17:00', open: true }, // Friday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Saturday
        ],
      },
    ],
  },
  {
    name: 'SJCG - Northwest Ontario',
    dfdLink: 'https://sjcgnwo.accessraam.ca/',
    website:
      'https://www.stjoes.ca/hospital-services/mental-health-addiction-services/rapid-access-addiction-medicine-raam-clinic',
    telephone: ['905-522-1155;35800'],
    locations: [
      {
        name: 'St. Joseph’s Healthcare Hamilton RAAM Clinic',
        lat: 43.249836,
        lng: -79.8716858,
        hours: 'Walk-in and scheduled appointments: Wed 8:00am-12noon.',
        address: '225 James St. S, Hamilton.',
        dfdHours: [
          { openHour: '00:00', closeHour: '23:59', open: false }, // Sunday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Monday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Tuesday
          { openHour: '08:00', closeHour: '12:00', open: true }, // Wednesday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Thursday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Friday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Saturday
        ],
      },
    ],
  },
  {
    name: 'The Brant Haldimand Norfolk',
    dfdLink: 'https://bhnraam.accessraam.ca/',
    website: 'https://www.raamclinics.com/',
    telephone: ['519-758-0008', '1-866-987-7226'],
    locations: [
      {
        name: 'Brant Haldimand Norfolk RAAM Clinic (Brantford)',
        lat: 43.139074,
        lng: -80.2575681,
        hours:
          'Clinic hours: Tues 9am-6pm, Wed 9am-1pm, Fri 9am-3pm. Virtual clinic hours: Tues 9am-6pm, Wed, Fri 9am-3pm.',
        address: '320 Colborne St. E, Brantford.',
        dfdHours: [
          { openHour: '00:00', closeHour: '23:59', open: false }, // Sunday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Monday
          { openHour: '09:00', closeHour: '18:00', open: true }, // Tuesday
          { openHour: '09:00', closeHour: '13:00', open: true }, // Wednesday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Thursday
          { openHour: '09:00', closeHour: '15:00', open: true }, // Friday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Saturday
        ],
      },
      {
        name: 'Brant Haldimand Norfolk RAAM Clinic (Dunnville)',
        lat: 42.9034578,
        lng: -79.6174205,
        hours:
          'Walk-in and scheduled appointments Mon 9:00am-3:00pm. Virtual clinic Mon 9:00am-4:00pm.',
        address: '140 Broad St. E., Dunnville, ON  (above Hauser’s Pharmacy)',
        dfdHours: [
          { openHour: '00:00', closeHour: '23:59', open: false }, // Sunday
          { openHour: '09:00', closeHour: '16:00', open: true }, // Monday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Tuesday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Wednesday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Thursday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Friday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Saturday
        ],
      },
      {
        name: 'Brant Haldimand Norfolk RAAM Clinic (Simcoe)',
        lat: 42.8368782,
        lng: -80.3049232,
        hours:
          'Walk-in, virtual and scheduled appointments: Thurs 9:00am-3:00pm.',
        address: '32 Robinson Street, Simcoe, Ontario, Canada',
        dfdHours: [
          { openHour: '00:00', closeHour: '23:59', open: false }, // Sunday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Monday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Tuesday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Wednesday
          { openHour: '09:00', closeHour: '15:00', open: true }, // Thursday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Friday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Saturday
        ],
      },
    ],
  },
  {
    name: 'The Royal',
    dfdLink: 'https://theroyal-staging.accessraam.m21lab.com/',
    website:
      'https://www.theroyal.ca/patient-care-information/clinics-services-programs/substance-use-and-concurrent-disorders',
    telephone: ['613-722-6521;6508'],
    locations: [
      {
        name: 'The Royal’s RAAM Clinic',
        lat: 45.3885381,
        lng: -75.7298003,
        hours:
          'Drop-in (in person and virtual): Mon-Thurs 8am-11:45am and 1pm-2:30pm; Fri 8am-1130am. Scheduled appointments: Mon-Wed 8am-4pm, Thurs-Fri 8am-3:30pm.',
        address: '1145 Carling Ave, Rm 2141, Ottawa',
        dfdHours: [
          { openHour: '00:00', closeHour: '23:59', open: false }, // Sunday
          { openHour: '08:00', closeHour: '16:00', open: true }, // Monday
          { openHour: '08:00', closeHour: '16:00', open: true }, // Tuesday
          { openHour: '08:00', closeHour: '16:00', open: true }, // Wednesday
          { openHour: '08:00', closeHour: '15:30', open: true }, // Thursday
          { openHour: '08:00', closeHour: '15:30', open: true }, // Friday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Saturday
        ],
      },
    ],
  },
  {
    name: 'Stonehenge Therapeutic Community',
    dfdLink: 'https://stonehengetc.accessraam.ca/',
    website: 'https://raamww.ca/',
    telephone: ['1-844-722-2977'],
    locations: [
      {
        name: 'Fergus RAAM Clinic',
        lat: 43.6990215,
        lng: -80.3893792,
        hours:
          'Walk-In appointments: Mon and Fri 9am-12pm and 1-4pm. Scheduled in person and virtual care available for follow-up appointments during regular clinic hours 9am-3pm. No drop-in virtual care available.',
        address:
          'Upper Grand Family Health Team,  Trailside Medical Centre  6420 Beatty Line N. (1st floor)  Fergus, Ontario N1M 2W3',
        dfdHours: [
          { openHour: '00:00', closeHour: '23:59', open: false }, // Sunday
          { openHour: '09:00', closeHour: '16:00', open: true }, // Monday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Tuesday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Wednesday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Thursday
          { openHour: '09:00', closeHour: '16:00', open: true }, // Friday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Saturday
        ],
      },
      {
        name: 'Rural Wellington Mobile Clinic and Digital Front Door – Palmerston',
        description: 'Also available in Arthur on Wed, 1-4pm.',
        lat: 43.8265294,
        lng: -80.8493687,
        hours: 'Drop in: Thurs 9:30am-3:30pm.',
        address: '520 Cavan Street, Palmerston, ON, Canada',
        dfdHours: [
          { openHour: '00:00', closeHour: '23:59', open: false }, // Sunday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Monday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Tuesday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Wednesday
          { openHour: '09:30', closeHour: '15:30', open: true }, // Thursday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Friday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Saturday
        ],
      },
      {
        name: 'Rural Wellington Mobile RAAM and Digital Front Door – Arthur',
        description:
          'Also available in Palmerston on Thurs 9:30am-3:30pm. Email: info@raamww.ca',
        lat: 43.8311666,
        lng: -80.5346859,
        hours: 'Drop-in: Wed 1-4 pm.',
        address: '110 Charles St E, Arthur, ON N0G 1A0',
        dfdHours: [
          { openHour: '00:00', closeHour: '23:59', open: false }, // Sunday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Monday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Tuesday
          { openHour: '13:00', closeHour: '16:00', open: true }, // Wednesday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Thursday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Friday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Saturday
        ],
      },
      {
        name: 'Sanguen Health Centre',
        lat: 43.5478799,
        lng: -80.2512732,
        hours:
          'Walk-in care Mon and Wed: 9am-12pm, 1-4pm. Scheduled in person and virtual care available for follow-up appointments during regular clinic hours 9am-4pm.',
        address: '176 Wyndham St. N (Lower level), Guelph.',
        dfdHours: [
          { openHour: '00:00', closeHour: '23:59', open: false }, // Sunday
          { openHour: '09:00', closeHour: '16:00', open: true }, // Monday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Tuesday
          { openHour: '09:00', closeHour: '16:00', open: true }, // Wednesday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Thursday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Friday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Saturday
        ],
      },
    ],
  },
  {
    name: "Women's College",
    dfdLink: 'https://womenscollege.accessraam.ca/',
    website:
      'https://www.womenscollegehospital.ca/care-programs/substance-use-service',
    telephone: ['416-323-7559;6'],
    locations: [
      {
        name: 'Women’s College Hospital',
        lat: 43.6615138,
        lng: -79.3871213,
        hours:
          'Walk-in, virtual and scheduled appointments. Mon, Tues, Thurs 10am-12pm. For walk-in visits (no appointment necessary), please arrive before 11:30 am. Call to book a RAAM visit; same day appointments are available.',
        address: '76 Grenville St., 3rd Floor, Toronto.',
        dfdHours: [
          { openHour: '00:00', closeHour: '23:59', open: false }, // Sunday
          { openHour: '10:00', closeHour: '12:00', open: true }, // Monday
          { openHour: '10:00', closeHour: '12:00', open: true }, // Tuesday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Wednesday
          { openHour: '10:00', closeHour: '12:00', open: true }, // Thursday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Friday
          { openHour: '00:00', closeHour: '23:59', open: false }, // Saturday
        ],
      },
    ],
  },
];

export const generateDFDClinic = (
  clinic: OFDClinic,
  location: Location | number
) => {
  if (typeof location === 'number') {
    location = clinic.locations[location];
  }
  return {
    dfdLink: clinic.dfdLink,
    website: clinic.website,
    telephone: clinic.telephone,
    ...location,
  };
};

export const DFD_CLINICS: DFDClinicDataType[] = OFD_CLINICS.flatMap((clinic) =>
  clinic.locations.map((location) => generateDFDClinic(clinic, location))
);

export const NON_DFD_CLINICS: ClinicDataType[] = [
  {
    name: 'Aegis Health Group',
    lat: 42.3025884,
    lng: -82.9961414,
    hours:
      'Walk-in and scheduled appointments: Mon-Fri 8am-5pm. Drop-in and scheduled virtual care also available during clinic hours.',
    website: 'https://www.aegishealth.ca/',
    address: '1883 Turner Road, Windsor, ON, Canada',
    telephone: ['226-946-1000'],
  },
  {
    name: 'Aegis Health Group',
    lat: 42.307277,
    lng: -83.032823,
    hours:
      'Walk-in and scheduled appointments: Mon-Fri 8am-5pm. Drop-in and scheduled virtual care also available during clinic hours.',
    website: 'https://www.aegishealth.ca/',
    address: '1083 Ouellette Avenue, Windsor, ON, Canada',
    telephone: ['226-946-1000'],
  },
  {
    name: 'Alliance Centre – West Nipissing General Hospital',
    lat: 46.3722287,
    lng: -79.9162421,
    hours:
      'Walk-in: Mon, Tues, Thurs, Fri 8:30am-4:30pm, Wed 8:30am-7pm. Scheduled appointments Tues 9am-12noon and Thurs 2-4:30pm.',
    website: 'https://www.wngh.ca/',
    address:
      'West Nipissing General Hospital, 172 Ethel Street, Unit 4, Sturgeon Falls, ON Canada',
    telephone: ['705-753-2271'],
  },
  {
    name: 'Anishnawbe Health Toronto',
    lat: 43.6607323,
    lng: -79.373192,
    hours: 'Walk-in, virtual and scheduled appointments. Mon-Fri 9am-5pm.',
    website: 'https://aht.ca/service/substance-abuse-supports/',
    address: '179 Gerrard St. E, Toronto.',
    telephone: ['416-657-0379;234'],
  },
  {
    name: 'Bluewater Methadone Clinic – Sarnia',
    lat: 42.9712548,
    lng: -82.4056469,
    hours:
      'Walk in and schedule appointments: Mon 9am-5pm, Tues and Fri 9am-4pm, Thurs 9am-6pm. (Walk-ins welcome until one hour before clinic closes.) Appointments are usually given for the next day the clinic is open. Virtual/phone: Mon 9am-6pm, Tues and Fri 9am-5pm, Thurs 9am-7pm.',
    website: 'https://bluewatermethadoneclinic.ca/raam-clinic/',
    address: '118 Victoria St. N, Sarnia',
    telephone: ['519-337-4357'],
  },
  {
    name: 'Brampton – CMHA Peel Dufferin William G. Davis Centre for Families',
    lat: 43.7030285,
    lng: -79.7252208,
    hours:
      'In-person and virtual drop-in hours: Tues 12-2pm and 4:30-6:30pm. NEW Sat clinic: 10am-1pm. Scheduled in-person and virtual appointments: Mon-Fri 8:30am-pm. RAAM virtual care appointments are organized with the client via direct phone call during the regular clinical hours. Clients who have not been registered are required to complete an application intake process and/or assessment in person which is administered by the NP or RN on site.',
    website:
      'https://cmhapeeldufferin.ca/programs/rapid-access-addiction-medicine-clinic-raam/',
    address: '60 West Dr., Unit 106, Brampton',
    telephone: ['905-451-2123'],
  },
  {
    name: 'Cambridge RAAM Clinic',
    lat: 43.3594619,
    lng: -80.3152144,
    hours:
      'Walk-in and scheduled appointments Monday and Friday 9:00am-3:00pm.',
    website:
      'https://houseoffriendship.org/addiction/rapid-access-addiction-medicine-clinic/',
    address: '13 Water St. N (Rear entrance), Cambridge.',
    telephone: ['519-957-5000', '519-742-8327;562'],
  },
  {
    name: 'Chatham-Kent Health Alliance RAAM Clinic',
    lat: 42.4050582,
    lng: -82.1936363,
    hours:
      'Walk-ins and scheduled appointments: Mon, Wed, Fri 12-4pm. Scheduled appointments only: Thurs 12-4pm. Virtual care available during opening hours.',
    website: 'https://www.ckha.on.ca/raam',
    address: '80 Grand Ave West, Zone F, Level 1, Chatham, ON N7M 5L9',
    telephone: ['519-352-6400;6740'],
  },
  {
    name: 'CKHA RAAM Clinic, Wallaceburg',
    lat: 42.5991912,
    lng: -82.3668235,
    hours: 'Drop-in hours: Tues 12-4pm.',
    website: 'https://www.ckha.on.ca/raam/',
    address: '325 Margaret Ave, Rm 102, Wallaceburg, ON N8A 2A7',
    telephone: ['519-352-6400;8311'],
  },
  {
    name: 'Clinic 217',
    lat: 42.779094,
    lng: -81.1949838,
    hours:
      'Walk-in and scheduled appointments: Mon 8:30-11:30am, 1-3:30pm, 3:45-7pm; Tues, Thurs 8:30-11:30am, Fri 8:30-11:30 and 1-3:30pm. New patient assessments and starts: Mon, Tues, Thurs and Fri 10am-1pm. Phone appointments available upon request.',
    website: 'https://www.southwesthealthline.ca/displayservice.aspx?id=185620',
    address: '475 Talbot St, St. Thomas.',
    telephone: ['519-631-9040'],
  },
  {
    name: 'Cochrane District RAAM clinic',
    lat: 48.4735837,
    lng: -81.3253813,
    hours:
      'Walk-in, Virtual and scheduled appointments: Mon, Wed 2-5pm, Fri 1-5pm. Walk-ins welcome based on staff availability.',
    website: 'https://www.scasinc.ca/', // Temporary info link, offers redirect to 'https://cmhact.ca/'
    address: '330 2nd Ave., Timmins, ON P4N 8A4',
    telephone: ['705-531-7226'],
  },
  {
    name: 'Cochrane District RAAM clinic - Satellite location',
    lat: 48.76941806544684,
    lng: -80.6846809729342,
    hours:
      'Virtual and scheduled appointments Fri 1-5pm. Walk-ins welcome based on staff availability.',
    website: 'https://www.scasinc.ca/', // Temporary info link, offers redirect to 'https://cmhact.ca/'
    address: '58A Anson Drive, Iroquois Falls',
    telephone: ['705-531-7226'],
  },
  {
    name: 'COMPASS (Concurrent Outpatient Medical & Psychosocial Addiction Support Service)',
    lat: 43.6433768,
    lng: -79.4190366,
    hours: 'Walk-in services: Mon-Fri 10am-noon.',
    website: 'https://www.camh.ca/en/your-care/programs-and-services/compass',
    address: 'CAMH, Bell Gateway Building, 3rd Floor, 100 Stokes St',
    telephone: ['416-535-8501'],
  },
  {
    name: 'Comprehensive Treatment Clinic',
    lat: 43.6815219,
    lng: -79.3334635,
    hours:
      'Walk-ins: Mon 1-3pm, Tues and Fri 12-2pm. Please call ahead if possible before walking in. Scheduled appointments: Thurs 1-3pm. Phone and virtual care appointments available upon request.',
    website: 'https://comprehensivetreatmentclinic.com/',
    address: '1154 Danforth Avenue, Toronto, ON M4J 1M3, Canada',
    telephone: ['647-490-4008'],
  },
  {
    name: 'Dilico RAAM Clinic',
    lat: 48.3542248,
    lng: -89.2612659,
    hours:
      'Drop-in: Mon, Fri, and every second Tues 9:30am-11:30am. Virtual drop-in and booked appointments: Mon, Tues, Fri 9:30am-11:30am.',
    website:
      'https://www.tbaycounselling.com/addiction-mental-health/rapid-access-addiction-medicine-clinic/',
    address: '200 Anemki Place, Fort William First Nation, Thunder Bay.',
    telephone: ['807-626-8478'],
  },
  {
    name: 'Duke St RAAM Clinic',
    lat: 43.4526429,
    lng: -80.4929146,
    hours: 'Walk-in hours Mon-Fri 9am-3pm.',
    website:
      'https://houseoffriendship.org/addiction/rapid-access-addiction-medicine-clinic/',
    address: '150 Duke Street West, Kitchener, ON, Canada',
    telephone: ['519-957-5000', '519-742-8327;562'],
  },
  {
    name: 'EYRND Mental Health and Addiction Wellness Centre',
    lat: 43.8792391,
    lng: -79.3135941,
    hours: 'Scheduled Appointments: Tues 1-4pm.',
    website:
      'https://www.eyrnd.ca/mental-health-and-addictions-wellness-centre',
    address: '9249 Kennedy Rd unit 101, Unionville, ON, Canada',
    telephone: ['905-758-2279'],
  },
  {
    name: 'Englehart & District Family Health Team – RAAM Clinic',
    lat: 47.8226775,
    lng: -79.8792617,
    hours:
      'Walk-in, virtual and scheduled appointments. Mon-Fri 9:00-3:00pm. RAAM services are provided within our Family Health Team. Our NP (provider) does a quick assessment of walk-ins, after which the client is scheduled for an in-person appointment within 48 hours. Clients who call to make an appointment are seen within 48 hours. New clients have in-person visits initially and then follow up appointments if suitable.',
    website: 'https://edfht.com/',
    address: '63 5th Street, Englehart, ON, Canada',
    telephone: ['705-544-2321'],
  },
  {
    name: 'GBHS RAAM Clinic',
    lat: 44.5600212,
    lng: -80.9255921,
    hours:
      'Booked appointments (in person or phone) only: Mon and Fri 9am-noon, Tues 8am-noon, Wed and Thurs 1pm-4pm.',
    website: 'https://www.gbhs.on.ca/mental-health-addiction-services/',
    address: '495 9th Ave. E, Owen Sound',
    telephone: ['519-376-5666;2'],
  },
  {
    name: 'Grand River Hospital RAAM Clinic',
    lat: 43.4570781,
    lng: -80.5102857,
    hours: 'Walk-in hours Mon-Fri 9am-3pm.',
    website:
      'https://houseoffriendship.org/addiction/rapid-access-addiction-medicine-clinic/',
    address: '850 King Street West, Kitchener, ON, Canada',
    telephone: ['519-957-5000', '519-742-8327;562'],
  },
  {
    name: 'Halton & Mississauga RAAM Clinic',
    lat: 43.405081554400844,
    lng: -79.8076666241141,
    hours:
      'Walk-in and scheduled appointments: Mon 9am-3:30pm, Tues 11:30am-6pm, Wed 9am-6pm, Thurs 9am-4pm, Fri 10am-3pm. New patients should arrive at least one hour before clinic closes. Virtual care also available.',
    website: 'https://hmraam.ca/',
    address: '2500 Appleby Line, Unit D10, Burlington, ON',
    telephone: ['1-888-388-RAAM'],
  },
  {
    name: 'Halton & Mississauga RAAM Clinic',
    lat: 43.6492247,
    lng: -79.9266377,
    hours:
      'Walk-in and scheduled appointments: Mon 9am-3:30pm, Tues 11:30am-6:30pm, Wed 9am-6pm, Thurs 9am-4pm, Fri 10am-3pm. New patients should arrive at least one hour before clinic closes. Virtual care also available.',
    website: 'https://hmraam.ca/',
    address:
      'Halton RAAM and Methadone Clinic – Georgetown, 118 Mill Street, Georgetown, ON, Canada',
    telephone: ['1-888-388-RAAM'],
  },
  {
    name: 'Halton & Mississauga RAAM Clinic',
    lat: 43.5175824,
    lng: -79.8776902,
    hours:
      'Walk-in and scheduled appointments: Mon 9am-3:30pm, Tues 11:30am-6pm, Wed 9am-6pm, Thurs 9am-4pm, Fri 10am-3pm. New patients should arrive at least one hour before clinic closes. Virtual care also available.',
    website: 'https://hmraam.ca/',
    address: '400 Main Street E, Unit 112, Milton',
    telephone: ['1-888-388-RAAM'],
  },
  {
    name: 'Halton & Mississauga RAAM Clinic',
    lat: 43.4787986,
    lng: -79.6601658,
    hours:
      'Walk-in and scheduled appointments: Mon 9am-3:30pm, Tues 11:30am-6:30pm, Wed 9am-6pm, Thurs 9am-4pm, Fri 10am-3pm. New patients should arrive at least one hour before clinic closes. Virtual care also available.',
    website: 'https://hmraam.ca/',
    address: '1525 Cornwall Road, Oakville, ON, Canada',
    telephone: ['1-888-388-RAAM'],
  },
  {
    name: 'Halton & Mississauga RAAM Clinic',
    lat: 43.4462308,
    lng: -79.6865702,
    hours:
      'Walk-in and scheduled appointments: Mon 9am-3:30pm, Tues 11:30am-6pm, Wed 9:30am-6pm, Thurs 9:30am-4pm, Fri 10am-3pm. New patients should arrive at least one hour before clinic closes. Virtual care also available.',
    website: 'https://hmraam.ca/',
    address: '496 Kerr Street, Oakville, ON, Canada',
    telephone: ['1-888-388-RAAM'],
  },
  {
    name: 'Halton & Mississauga RAAM Clinic',
    lat: 43.5291056,
    lng: -79.639026,
    hours:
      'Walk-in and scheduled appointments: Mon 9am-3:30pm, Tues 11:30am-6:30pm, Wed 9:30am-6pm, Thurs 9:30am-4pm, Fri 10am-3pm. New patients should arrive at least one hour before clinic closes. Virtual care also available.',
    website: 'https://hmraam.ca/',
    address: '1603 Clarkson Road North, Mississauga, ON, Canada',
    telephone: ['1-888-388-RAAM'],
  },
  {
    name: 'Kapuskasing RAAM Clinic',
    lat: 49.4149643,
    lng: -82.4197823,
    hours: 'Drop-in hours: Mon and Thurs 1:00pm-3:30pm. Phone/virtual: same.',
    website: 'https://www.addictionservicestoxicomanie.com/',
    address: '27 Kolb Avenue, Kapuskasing ON P5N 1G2',
    telephone: ['705-335-8408'],
  },
  {
    name: 'Kenora RAAM Clinic',
    lat: 49.7662803,
    lng: -94.4886965,
    hours: 'Walk-in and scheduled appointments: Mon-Fri 8am-4pm.',
    website: 'http://www.lwdh.on.ca/', // https not available
    address: 'Suite 5 – 308 Second Street South, kenora',
    telephone: ['807-464-6397'],
  },
  {
    name: 'Kirkland Lake RAAM Clinic',
    lat: 48.149927,
    lng: -80.0328673,
    hours: 'Scheduled appointments Tues 9-11am.',
    website: 'https://cmhact.ca/',
    address: '7 Kirkland St W, Kirkland Lake, ON, P2N 1N9', // It's actually 5 Kirkland, but Google Maps doesn't think that building exists
    telephone: ['705-567-9596'],
  },
  {
    name: 'Leamington Clinic',
    description: '24/7 emergency line for urgent care: 519-257-0981',
    lat: 42.0540881,
    lng: -82.6011275,
    hours:
      'Walk-in and scheduled appointments: Thurs 1-4:30pm. Virtual care available same times.',
    website: 'https://www.roseraam.ca/',
    address: '15 John St, Leamington.',
    telephone: ['519-398-9199'],
  },
  {
    name: 'Marathon Family Health Team RAAM Clinic',
    lat: 48.7180291,
    lng: -86.3762527,
    hours:
      'Walk-in, virtual and scheduled appointments. Tues and Thurs 12:20-4:30PM.',
    website: 'https://mfht.org/',
    address: '22 Peninsula Road, Marathon, ON, Canada',
    telephone: ['807-229-3243'],
  },
  {
    name: 'Maternity Centre of Hamilton (PROSPR program)',
    lat: 43.2569357,
    lng: -79.8735212,
    hours:
      'Walk-in and scheduled appointments: Wed 1-4pm. Virtual care available only in exceptional circumstances.',
    website: 'https://mch.mcmaster.ca/services/prospr/',
    address: '100 Main St. W, 3rd Floor (For pregnant patients), Hamilton.',
    telephone: ['905-528-5553'],
  },
  {
    name: 'Michael Garron Hospital',
    description: 'Email: raamclinic@tehn.ca',
    lat: 43.6898713,
    lng: -79.3248582,
    hours:
      'Walk-in (in person and virtual): Tues, Thurs, Fri 1-3pm. Scheduled appointments: Mon, Tues 8am-3pm; Wed 8am-4pm; Thurs, Fri 1-3pm.',
    website:
      'https://www.tehn.ca/programs-services/mental-health-addiction/addiction-services/rapid-access-addiction-medicine-clinic',
    address: '825 Coxwell Ave., 1st Floor (Admitting/Registration), Toronto.',
    telephone: ['416-469-6580;2517'],
  },
  {
    name: 'Muskoka RAAM Clinic',
    lat: 45.043942,
    lng: -79.315419,
    hours:
      'Walk-in and scheduled appointments: Mon-Fri 8:30am-4:30pm. Scheduled virtual care: M, T, F 9am-12pm and W, Th 1-4pm.',
    website:
      'https://mps.cmha.ca/programs-services/raam-rapid-access-addiction-medicine/',
    address: '173 Manitoba St., Suite 202, Bracebridge.',
    telephone: ['705-645-2262', '1-800-245-5036'],
  },
  {
    name: 'Niagara Health RAAM Clinic',
    lat: 43.1677914,
    lng: -79.2332201,
    hours: 'Drop-in: Mon 9am-4pm.',
    website: 'https://www.niagarahealth.on.ca/site/addictionrecoveryservices',
    address: '264 Welland Avenue, St. Catharines, ON, Canada',
    telephone: ['905-378-4647'],
  },
  {
    name: 'Niagara Health RAAM – Community Addiction Services of Niagara (CASON) Walk in Clinic',
    lat: 43.1590255,
    lng: -79.2452975,
    hours: 'Drop-in: Tues 12-4pm.',
    website: 'https://www.niagarahealth.on.ca/site/addictionrecoveryservices',
    address: '60 James Street, Suite 404, St. Catharines, ON L2R 7E7',
    telephone: ['905-378-4647;49463'],
  },
  {
    name: 'Niagara Health RAAM – Niagara Falls Hospital',
    lat: 43.0939334,
    lng: -79.0895715,
    hours: 'No walk-in hours. Scheduled appointments: Mon-Fri 8am-4pm.',
    website: 'https://www.niagarahealth.on.ca/site/addictionrecoveryservices',
    address: '5546 Portage Road, Niagara Falls, ON L2E 6X2',
    telephone: ['905-378-4647;49463'],
  },
  {
    name: 'Niagara Health RAAM – St. Catharines',
    lat: 43.153663,
    lng: -79.2785004,
    hours: 'No drop-in hours. Booked appointments: Mon-Fri, 8am-4pm.',
    website: 'https://www.niagarahealth.on.ca/site/addictionrecoveryservices',
    address: '1200 Fourth Ave., St. Catharines, ON L2S 0A9',
    telephone: ['905-378-4647;49463'],
  },
  {
    name: 'Niagara Health RAAM – Welland Hospital site',
    lat: 42.9807228,
    lng: -79.2512236,
    hours: 'No drop-in hours. Booked appointments Mon-Fri, 8am-4pm.',
    website: 'https://www.niagarahealth.on.ca/site/addictionrecoveryservices',
    address: '65 Third Street, Welland, ON L3B 4W6',
    telephone: ['905-378-4647;49463'],
  },
  {
    name: 'North Bay Regional Health Centre RAAM Clinic',
    lat: 46.3153379,
    lng: -79.454043,
    hours:
      'Walk-in, virtual and scheduled appointments Mon-Fri 8:30am-4pm. Scheduled appointments with physician Mon 2-5pm.',
    website: 'https://www.connexontario.ca/Program-Details?ProgramId=35577',
    address: '120 King St. West, North Bay, ON',
    telephone: ['1-833-434-7800'],
  },
  {
    name: 'North Simcoe Muskoka RAAM Clinic',
    lat: 44.3919901,
    lng: -79.6998006,
    hours:
      'In-person and virtual drop in hours: Mon-Sat 8:30 am-3:30 pm. Scheduled in-person, virtual or phone appointments: Mon-Sat 8:30 am – 3:30 pm.',
    website:
      'https://www.rvh.on.ca/areas-of-care/mental-health-and-addictions/adult-addiction-services/',
    address: '70 Wellington St. W, Lower Level, Barrie.',
    telephone: ['1-833-797-3095', '705-797-3095'],
  },
  {
    name: 'North Simcoe Muskoka RAAM Clinic',
    lat: 44.6053465,
    lng: -79.411273,
    hours:
      'Scheduled virtual and in-person appointments: Mon-Fri 8:30am-3:30pm.',
    website:
      'https://www.rvh.on.ca/areas-of-care/mental-health-and-addictions/adult-addiction-services/',
    address: '169 Front St. South, 1st Floor (Use side entrance), Orillia.',
    telephone: ['1-833-797-3095', '705-797-3095'],
  },
  {
    name: 'North Simcoe Muskoka Regional RAAM Clinic',
    lat: 44.4685371,
    lng: -80.0758862,
    hours:
      'Drop-in, virtual and scheduled appointments: Mon-Fri 8:30am-3:30pm.',
    website:
      'https://www.rvh.on.ca/areas-of-care/mental-health-and-addictions/adult-addiction-services/',
    address:
      'South Georgian Bay Community Health Centre, 14 Ramblewood Dr., Unit 202, 2nd Floor, Wasaga Beach.',
    telephone: ['1-833-797-3095'],
  },
  {
    name: 'North Simcoe Muskoka Regional RAAM – Chigamik Community Health Centre',
    lat: 44.7557393,
    lng: -79.8829725,
    hours:
      'Drop-in (in person and virtual), and scheduled appointments: Mon-Fri 8:30am-3:30pm.',
    website:
      'https://www.rvh.on.ca/areas-of-care/mental-health-and-addictions/adult-addiction-services/',
    address: '287 Bayshore Dr., 2nd Floor, Midland.',
    telephone: ['1-833-797-3095'],
  },
  {
    name: 'Oasis RAAM program',
    lat: 45.4293799,
    lng: -75.6835995,
    hours: 'Drop-in and appointments: Mon-Fri 9am-2pm.',
    website: 'https://www.sandyhillchc.on.ca/',
    address: '221 Nelson St, Ottawa, Ontario K1N 1C7, Canada',
    telephone: ['613-569-3488;2106'],
  },
  {
    name: 'Oxford County RAAM Clinic',
    lat: 43.1287671,
    lng: -80.7564126,
    hours:
      'Walk-ins and scheduled appointments: Mon 9-11am and Tues 1-3pm. Phone appointments Mon-Fri 9am-3pm.',
    website: 'https://www.oxfordraam.ca/',
    address: '35 Metcalf St., 2nd floor, Woodstock',
    telephone: ['519-539-1111'],
  },
  {
    name: 'Oxford County RAAM Clinic - Satellite location',
    lat: 43.03810493990238,
    lng: -80.88166362059052,
    hours:
      'Phone appointments Mon-Fri 9am-3pm. Scheduled appointments Wed 9-11am.',
    website: 'https://www.oxfordraam.ca/',
    address: '19 King St. East, Ingersoll',
    telephone: ['519-539-1111'],
  },
  {
    name: 'PAARC – CMHA Peel Dufferin',
    lat: 43.6385565,
    lng: -79.6259097,
    hours: 'Drop-in hours: Tues 10am-1pm. Virtual/phone: Mon-Fri 8:30am-4pm.',
    website:
      'https://cmhapeeldufferin.ca/programs/rapid-access-addiction-medicine-clinic-raam/',
    address:
      'Peel Addiction Assessment and Referral Centre (PAARC), 5170 Dixie Road, Suite 302, Mississauga, ON L4W 1E3',
    telephone: ['905-451-2123;2'],
  },
  {
    name: 'Parry Sound RAAM Clinic',
    lat: 45.34053506757901,
    lng: -80.01691193480156,
    hours:
      'Scheduled appointments: Mon 10am-6pm and Thurs 8:30am-4pm. No drop-in hours.',
    website: 'https://www.wpshc.com/',
    address:
      'West Parry Sound Health Centre, 1st floor Mental Health Hub,  6 Albert St. Parry Sound, ON',
    telephone: ['705-746-4540;1339'],
  },
  {
    name: 'Peel Youth Village – CMHA Peel Dufferin',
    lat: 43.6053746,
    lng: -79.6449912,
    hours:
      'Walk-in Wed 12-2pm and 4:30-6:30pm. Virtual/phone appointments Mon-Fri 8:30am-4pm.',
    website:
      'https://cmhapeeldufferin.ca/programs-services/rapid-access-addiction-medicine-clinic-raam/',
    address: '99 Acorn Pl, Mississauga.',
    telephone: ['905-451-2123', '1-877-451-2123;2'],
  },
  {
    name: 'Peterborough RAAM',
    lat: 44.3040588,
    lng: -78.3197832,
    hours:
      'Walk-in and scheduled appointments: Mon, Wed, Fri 8am-12pm. Virtual appointments available: Lindsay – Thurs 12-4pm, Coburg – Tues 12:30-3:30pm.',
    website:
      'https://www.prhc.on.ca/healthcare-services/mental-health-addictions/rapid-access-addiction-medicine-clinic/',
    address: '360 George St N, Peterborough, ON, Canada',
    telephone: ['705-749-9708'],
  },
  {
    name: 'RAAM Clinic Orangeville – CMHA Peel Dufferin',
    lat: 43.9212702,
    lng: -80.0993215,
    hours:
      'Walk-in: Thurs 12-2pm and 4:30-6:30pm. Virtual drop in: Mon-Fri 8:30am-4:00pm. Scheduled in-person appointments Thurs 9-10am,1-4pm. RAAM virtual care appointments are organized with the client via direct phone call during the regular clinical hours stated for each clinic location. Clients who have not been registered are required to complete an application intake process and/or assessment in person which is administered by the NP or RN on site.',
    website:
      'https://cmhapeeldufferin.ca/programs-services/rapid-access-addiction-medicine-clinic-raam/',
    address: '1 Elizabeth St. Unit L-2, Orangeville.',
    telephone: ['905-451-2123', '1-877-451-2123'],
  },
  {
    name: 'RAAM Clinic – CMHA Thames Valley Addiction and Mental Health Services.',
    lat: 42.9857105,
    lng: -81.2487554,
    hours:
      'Walk-ins Mon and Thurs 8:30am-3:30pm, Tues 8:30-11:30am, Wed 7:30-10:30am.',
    website: 'https://cmhatv.ca/programs-services/raam-clinic/',
    address: 'Addiction Services of Thames Valley, 200 Queens Ave. Suite 260',
    telephone: ['519-673-3242;1281'],
  },
  {
    name: 'RAAM Clinic – Joseph Brant Hospital',
    lat: 43.3168728,
    lng: -79.802229,
    hours: 'Drop-in clinic Hours: Thurs 1-2 pm.',
    website: 'https://www.josephbranthospital.ca/',
    address:
      'Joseph Brant Hospital, 1245 Lakeshore Rd, Burlington, ON, L7S 0A2',
    telephone: ['905-632-3737;3401'],
  },
  {
    name: 'RAAM-Greenstone/Longlac Site',
    lat: 49.7805055773698,
    lng: -86.53522066204181,
    hours:
      'Drop-in Hours: Mon-Thurs 9am-4pm, Fri 9am-12pm. Virtual/phone: Mon and Wed 1pm-4pm, Tues and Thurs 9am-4pm, Fri 9am-12pm.',
    // Missing website
    address: 'Dilico Anishinabek Family Care, 118 Forestry, Longlac',
    telephone: ['1-807-876-2271;132', '1-807-876-2271;148'],
  },
  {
    name: 'Rainy River District RAAM',
    lat: 48.6070103,
    lng: -93.3922754,
    hours:
      'Walk-in, virtual and scheduled appointments: Tues 1-4pm and Thurs 12-2pm.',
    // Missing website
    address: 'Riverside Health Care, 206 Victoria Avenue, Fort Frances, ON',
    telephone: ['1-807-274-2042;6230', '1-807-274-4807;1'],
  },
  {
    name: 'Recovery Care Bells Corners',
    lat: 45.3236011,
    lng: -75.8270094,
    hours: 'Walk-in, same day services: Mon-Fri 8:00AM-4:00PM.',
    website: 'https://recovery.care/',
    address: '2006 Robertson Road, Nepean, ON, Canada',
    telephone: ['613-903-6006'],
  },
  {
    name: 'Recovery Care Byward Market',
    lat: 45.4288486,
    lng: -75.6851983,
    hours: 'Walk-in, same day services: Mon-Fri 8:00AM-4:00PM.',
    website: 'https://recovery.care/',
    address: '306 Rideau Street, Ottawa, ON, Canada',
    telephone: ['613-454-0321'],
  },
  {
    name: 'Recovery Care Vanier Clinic',
    lat: 45.4335667,
    lng: -75.6687126,
    hours:
      'Walk-in, same day services: Mon-Fri 8am-4pm. Virtual care available same times.',
    website: 'https://recovery.care',
    address: '45 Montréal Road, Vanier, ON, Canada',
    telephone: ['613-763-6882'],
  },
  {
    name: 'Rexdale – CMHA Peel Dufferin',
    lat: 43.7465035,
    lng: -79.581531,
    hours: 'Drop-in Hours: Mon 10am-1pm. Virtual/phone: Mon-Fri 8:30am-4pm.',
    website:
      'https://cmhapeeldufferin.ca/programs/rapid-access-addiction-medicine-clinic-raam/',
    address: 'Rexdale Community Hub 15-21 Panorama Crt., Etobicoke, ON M9V 4E3',
    telephone: ['905-451-2123;2'],
  },
  {
    name: 'Roseville RAAM Clinic (City Centre)',
    description: '24/7 emergency line for urgent care 519-257-0981',
    lat: 42.3169289,
    lng: -83.0296837,
    hours:
      'Walk-in and scheduled appointments: Mon 8:30am-4:30pm, Sat 8:30am-12noon. Virtual care available same times.',
    website: 'https://www.roseraam.ca/',
    address: '500 Wyandotte St. E, Windsor.',
    telephone: ['519-254-3300'],
  },
  {
    name: 'Roseville RAAM Clinic (Roseville Gardens)',
    description: '24/7 emergency line for urgent care 519-257-0981',
    lat: 42.3100846,
    lng: -82.94419,
    hours:
      'Walk-in and scheduled appointments: Mon 8:30am-4:30pm, Thurs 8:30am-12noon. Virtual care available same times.',
    website: 'https://www.roseraam.ca/',
    address: '2525 Roseville Garden Dr. (Suite 100), Windsor',
    telephone: ['519-988-1020'],
  },
  {
    name: 'Sault area Hospital RAAM – Sault Ste Marie',
    lat: 46.5083752,
    lng: -84.3273701,
    hours:
      'Walk-in, virtual, and scheduled appointments: Mon 8am-6pm; Tues and Wed 8am-7pm; Thurs and Fri 8am-4pm.',
    website:
      'https://sah.on.ca/programs-services/mental-health-addictions/addictions-services/',
    address: '123 East St, Sault Ste. Marie.',
    telephone: ['705-759-3434;7200'],
  },
  {
    name: 'Scarborough Health Network RAAM Clinic',
    lat: 43.7561929,
    lng: -79.2469386,
    hours:
      'Scheduled in-person and virtual appointments only: Mon, Tues, Thurs, Fri 9:30am-12:30pm, RN/Counsellor appointments: Mon-Fri 8:30-4:30.',
    website: 'https://www.shn.ca/mental-health/addictions-program/',
    address:
      '3050 Lawrence Ave. E, Crockford Pavilion, Ground Floor, Scarborough M1P 2V5',
    telephone: ['416-431-8269'],
  },
  {
    name: 'Smooth Rock Falls RAAM Clinic',
    lat: 49.2746248,
    lng: -81.6118906,
    hours:
      'Drop-in (in person and virtal) and scheduled appointments: Thurs 1:00-3:30pm.',
    website: 'https://www.addictionservicestoxicomanie.com/',
    address: '107 Kelly Road, Smooth Rock Falls, ON P0L 2B0',
    telephone: ['705-272-3059'],
  },
  {
    name: 'St. Joseph’s Health Centre – Unity Health',
    lat: 43.63982695108545,
    lng: -79.44980113155394,
    hours:
      'Open Tuesdays and Thursdays 10:00–11:30am and Wednesdays 1:00–4:00pm.',
    website:
      'https://unityhealth.to/areas-of-care/programs-and-clinics/mental-health-and-addictions/#st-josephs-health-centre-addictions-services-0',
    address:
      'Family Medicine Centre, East wing, 30 The Queensway, Toronto, ON M6R 1B5',
    telephone: ['416-530-6486;3969'],
  },
  {
    name: 'St. Michael’s Hospital – Rapid Access Clinic (RAC)',
    description:
      'Virtual care through Coordinated Access to Addictions Services (CAAS) central number: 1-855-505-5045',
    lat: 43.6537678,
    lng: -79.3783212,
    hours: 'Walk-in and scheduled appointments: Mon-Fri 9am-12pm. ',
    website:
      'https://unityhealth.to/areas-of-care/programs-and-clinics/mental-health-and-addictions/',
    address: '30 Bond St., 17th Floor, Cardinal Carter South Wing, Toronto.',
    telephone: ['416-864-3082'],
  },
  {
    name: 'Street Health Centre, Kingston Community Health Centre',
    lat: 44.2337617,
    lng: -76.4831573,
    hours: 'Walk-in and scheduled appointments Mon-Fri 9am-4pm.',
    website: 'https://kchc.ca/barrack-street/methadone/',
    address: '115 Barrack St, Kingston.',
    telephone: ['613-549-1440'],
  },
  {
    name: 'Sunnybrook Health Sciences Centre',
    lat: 43.72122161397529,
    lng: -79.37645549893796,
    hours:
      'Drop-in: Mon 5-7pm (at Vibrant Healthcare Alliance, The Anne Johnston Health Station, 2398 Yonge St.), Wed 1-4pm, Fri 9am-12pm. Booked appointments: Mon 5-7pm, Tues, Thurs 9am-5pm, Wed 1-4pm, Fri 9am-12pm.',
    website: 'https://sunnybrook.ca/content/?page=bsp-rapid-addiction-medicine',
    address: '2075 Bayview Ave., A-wing, Room A146, Toronto, M4N 3M5',
    telephone: ['416-480-6736'],
  },
  {
    name: 'Thunder Bay RAAM – Norwest Community Health Centres site',
    lat: 48.3912114,
    lng: -89.2374571,
    hours:
      'Walk-in, scheduled and virtual appointments available during clinic hours: Mon-Thurs 2:00pm-5:00pm.',
    // website: 'https://www.norwestchc.org/locations/thunder-bay/programs/raam-clinic', // 404
    website: 'https://norwestchc.org/programs-services/raam/', // Might be the intended page?
    address: '525 Simpson St, Thunder Bay.',
    telephone: ['807-626-8478'],
  },
  {
    name: 'Timiskaming Shores RAAM Clinic',
    lat: 47.5082172,
    lng: -79.6705105,
    hours: 'Scheduled appointments: Tues 9-11am.',
    website: 'https://cmhact.ca/',
    address: '20 May Street South, New Liskeard, ON',
    telephone: ['705-647-4444'],
  },
  {
    name: 'UHN RAAM Clinic, Toronto Western Hospital',
    lat: 43.6534088,
    lng: -79.405247,
    hours:
      'Walk-in and drop-in virtual care: Mon-Wed 9:00-11:00am, Thurs and Fri 1:30-3:30pm.',
    website:
      'https://www.uhn.ca/MentalHealth/Clinics/Rapid_Access_Addiction_Medicine/Pages/default.aspx',
    address: '399 Bathurst St., 1st Floor, West Wing (WW 1-414), Toronto.',
    telephone: ['416-726-5052'],
  },
  {
    name: 'Wawa Family Health Team',
    lat: 48.0008679,
    lng: -84.772074,
    hours:
      'Walk-in, virtual, and scheduled appointments: Tues, Thurs 8:30-10:30am.',
    website: 'http://www.wawafamilyhealthteam.com/', // https not available
    address: '17 Government Rd. (Wawa Family Health Team clinic), Wawa',
    telephone: ['705-856-1313'],
  },
];

export const CLINIC_DATA: ClinicDataType[] = [
  ...DFD_CLINICS,
  ...NON_DFD_CLINICS,
];

export default CLINIC_DATA;
